import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";

const mdPlugins = {
  remarkPlugins: [remarkMath],
  rehypePlugins: [rehypeKatex],
};

interface MarkdownInlineProps {
  children: string;
  className?: string;
}

const SUPERSCRIPT_CHARACTERS: Record<string, string> = {
  "-": "\u207b",
  "0": "\u2070",
  "1": "\u00b9",
  "2": "\u00b2",
  "3": "\u00b3",
  "4": "\u2074",
  "5": "\u2075",
  "6": "\u2076",
  "7": "\u2077",
  "8": "\u2078",
  "9": "\u2079",
};

/**
 * Doubt Buddy occasionally sends bare LaTeX and serializes `\times` as the
 * JSON `\t` escape. Keep those answers readable even when no math delimiters
 * survive for remark-math to recognize.
 */
function normalizeBareMath(content: string): string {
  return content
    .replace(/\times\b/g, "\u00d7")
    .replace(/\\times\b/g, "\u00d7")
    .replace(/\\(?:cdot|bullet)\b/g, "\u00b7")
    .replace(/\\leq?\b/g, "\u2264")
    .replace(/\\geq?\b/g, "\u2265")
    .replace(/\\neq\b/g, "\u2260")
    .replace(
      /([A-Za-z0-9)\]])\^(-?\d+)/g,
      (_match: string, base: string, exponent: string) => {
        const superscript = [...exponent]
          .map((character) => SUPERSCRIPT_CHARACTERS[character])
          .join("");
        return superscript ? `${base}${superscript}` : _match;
      },
    );
}

export function normalizeMarkdownMath(content: string): string {
  const source = normalizeBareMath(content)
    .replace(/\r\n/g, "\n")
    .replace(/\\\[([\s\S]*?)\\\]/g, (_match, expression: string) => {
      const trimmed = expression.trim();
      return trimmed ? `\n\n$$\n${trimmed}\n$$\n\n` : "";
    })
    .replace(/\\\(([\s\S]*?)\\\)/g, (_match, expression: string) => {
      const trimmed = expression.trim();
      return trimmed ? `$${trimmed}$` : "";
    })
    .split("\n")
    .map((line) => {
      const withoutCodeIndent = line.replace(/^\s{4,}/, "");
      return withoutCodeIndent.replace(/^\*\s+/, "- ");
    })
    .join("\n");
  let normalized = "";
  let cursor = 0;

  while (cursor < source.length) {
    const open = source.indexOf("$$", cursor);
    if (open === -1) {
      normalized += source.slice(cursor);
      break;
    }

    const close = source.indexOf("$$", open + 2);
    if (close === -1) {
      normalized += source.slice(cursor);
      break;
    }

    normalized += source.slice(cursor, open);
    const expression = source.slice(open + 2, close).trim();
    if (expression) {
      normalized += `\n\n$$\n${expression}\n$$\n\n`;
    }
    cursor = close + 2;
  }

  return normalized.replace(/\n{3,}/g, "\n\n").trim();
}

export function MarkdownInline({ children, className }: MarkdownInlineProps) {
  const content =
    typeof children === "string" ? normalizeMarkdownMath(children) : "";
  if (!content.trim()) return null;
  return (
    <div className={className ?? "gac-md-inline"}>
      <ReactMarkdown {...mdPlugins}>{content}</ReactMarkdown>
    </div>
  );
}
